/**
 * Custom hook for handling suggested Linkers
 * Improved algorithm for random suggestions
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../core/contexts/AuthContext';
import suggestionService from '../services/suggestionService';
import connectionService from '../../connect/services/connectionService';

/**
 * Hook for fetching and managing suggested Linkers
 * 
 * @param {number} count - Number of suggestions to fetch
 * @param {Object} filters - Optional filters for suggestions
 * @returns {Object} Suggestions state and functions
 */
const useSuggestions = (count = 5, filters = {}) => {
  const { currentUser } = useAuth();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [connectionStates, setConnectionStates] = useState({});
  const [hasRandomSuggestions, setHasRandomSuggestions] = useState(false);

  // Fetch suggestions from Firebase
  const fetchSuggestions = useCallback(async () => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      // First try to get matched suggestions
      const suggestionsData = await suggestionService.fetchSuggestions(count, filters);
      
      // If no matched suggestions, try random suggestions
      if (suggestionsData.length === 0) {
        const randomSuggestions = await suggestionService.fetchRandomSuggestions(count);
        setSuggestions(randomSuggestions);
        setHasRandomSuggestions(true);
      } else {
        setSuggestions(suggestionsData);
        setHasRandomSuggestions(suggestionsData.some(s => s.isRandom));
      }
      
      // Check connection status for all suggestions
      const states = {};
      for (const suggestion of suggestions) {
        const status = await connectionService.getConnectionStatus(currentUser.uid, suggestion.id);
        states[suggestion.id] = status.status;
      }
      setConnectionStates(states);
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching suggestions:', err);
      setError(err.message || 'Failed to fetch suggestions');
      setLoading(false);
    }
  }, [currentUser, count, filters]);

  // Initialize on mount and when dependencies change
  useEffect(() => {
    fetchSuggestions();
  }, [fetchSuggestions]);

  // Send connection request
  const sendConnectionRequest = async (recipientId, message = '') => {
    if (!currentUser) return;
    
    try {
      await connectionService.sendConnectionRequest(recipientId, message);
      
      // Update connection state for this suggestion
      setConnectionStates(prev => ({
        ...prev,
        [recipientId]: 'pending'
      }));
      
      return true;
    } catch (error) {
      console.error('Error sending connection request:', error);
      throw error;
    }
  };

  return {
    suggestions,
    loading,
    error,
    connectionStates,
    hasRandomSuggestions,
    refreshSuggestions: fetchSuggestions,
    sendConnectionRequest
  };
};

export default useSuggestions;